import React from 'react';
import { Container, Header, Segment, Card, Message, List, Button, Icon } from 'semantic-ui-react';
import { observer } from 'mobx-react';
import '../Playbook.css';

const CompPlans = observer(() => {
    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = '/downloads/sample-comp-plan.xlsx';
        link.download = 'sample-comp-plan.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Container className="playbook-container">
            <h1 className="ui icon center aligned header playbook-branded-header" style={{fontFamily: 'Montserrat', textTransform: 'uppercase', color: 'rgb(35, 37, 58)', fontWeight: 600, fontSize: '38px', marginBottom: '-9px'}}>
                <i aria-hidden="true" className="icon">
                    <img src="/static/media/logo-icon.7bb8994723bbdcd674f1.png" alt="logo-icon" style={{height: '68px', marginBottom: '-15px'}} />
                </i>
                Skills/Leadership
            </h1>
            <div className="ui divider"></div>

            <Segment className="playbook-section">
                <Header as="h2" className="playbook-section-header">
                    Understanding Compensation Plans
                </Header>
                
                <p className="playbook-text">
                    This is a very broad topic with many ways to approach it. There are all sorts of producers, 
                    from lead generators to account managers to new account hunters. As a salesperson, we want 
                    to get paid for conducting and fulfilling the right activity. Those activities can change 
                    for us based on our role and/or based on the needs of the company.
                </p>

                <Message warning>
                    <Message.Header>Critical Understanding</Message.Header>
                    <p>
                        The thing every organization, regardless of size, needs to understand and clearly state 
                        to its sales team is that <strong>COMP PLANS ARE YEAR-TO-YEAR AND THEY WILL CHANGE</strong>.
                    </p>
                </Message>

                <p className="playbook-text">
                    Compensation plans drive behavior and activity. There are a multitude of reasons why 
                    compensation plans change. In some years, an organization will need to address a 
                    client-concentration issue and, as such, will heavily incent new work from new customers. 
                    However, new customers can put pressure on the organization. The easiest dollar to get 
                    is the next dollar from the current customer. Further, it is typically easier for a 
                    company to deliver for an existing customer than a new customer. Therefore, there are 
                    times when a company may more heavily incent growth within existing accounts over new logos.
                </p>
            </Segment>

            <Segment className="playbook-section">
                <Header as="h2" className="playbook-section-header">
                    Multifaceted Compensation Structure
                </Header>
                
                <p className="playbook-text">
                    Based on this information, we like compensation plans that give the sales team member 
                    multiple ways to "win". To do this, you can do all of the following:
                </p>

                <Card fluid>
                    <Card.Content>
                        <Card.Header>Three-Tier Compensation Model</Card.Header>
                        <List ordered size="large">
                            <List.Item>
                                <List.Content>
                                    <List.Header>Revenue Acquisition Based on Goal</List.Header>
                                    <List.Description>
                                        Pay for revenue acquisition based on a goal. This includes a tiered 
                                        percentage of payout based on how close the salesperson gets to target.
                                    </List.Description>
                                </List.Content>
                            </List.Item>
                            <List.Item>
                                <List.Content>
                                    <List.Header>New Client Acquisition Fee</List.Header>
                                    <List.Description>
                                        Pay a flat fee for new client acquisition.
                                    </List.Description>
                                </List.Content>
                            </List.Item>
                            <List.Item>
                                <List.Content>
                                    <List.Header>Team-Based Bonus</List.Header>
                                    <List.Description>
                                        Pay a discretionary, team-based bonus if the company hits its targets.
                                    </List.Description>
                                </List.Content>
                            </List.Item>
                        </List>
                    </Card.Content>
                </Card>

                <Message info>
                    <p>
                        Using a multifaceted comp plan gives the salesperson a chance to make money throughout 
                        the year rather than only on a percentage of the sale value. Plus, the team-based 
                        bonus can promote collaboration across the team.
                    </p>
                </Message>

                <p className="playbook-text">
                    The key is to keep it manageable. Comp plans can easily become very complicated and 
                    ultimately unmanageable. We would say keep it simple, but nothing about managing people 
                    and their pay is simple. So we will go with <strong>manageable</strong>.
                </p>
            </Segment>

            <Segment className="playbook-section">
                <Header as="h2" className="playbook-section-header">
                    How Much Should a Salesperson Make?
                </Header>
                
                <p className="playbook-text">
                    The answer is "whatever the market rate is." When it comes to compensation, sales team 
                    members should have uncapped potential. But to start, the sales team member will have a:
                </p>

                <List bulleted size="large">
                    <List.Item>a base salary</List.Item>
                    <List.Item>a variable pay structure</List.Item>
                </List>

                <p className="playbook-text">
                    He or she also has a market value based on a number of factors (location, experience, etc.). 
                    The company should define what the salesperson should make (comp + variable) if he or she 
                    meets goals exactly. Then the model should include accelerators if the salesperson exceeds 
                    those goals. Not having accelerators and/or uncapped potential can cause a salespeople to 
                    govern their sales once they meet goals. But if they have an opportunity to "juice" their 
                    earnings while the company exceeds financial expectations, then everyone will win.
                </p>

                <Message positive>
                    <Message.Header>Key Principle</Message.Header>
                    <p>
                        Always include accelerators and uncapped potential to prevent salespeople from 
                        governing their sales once they meet basic goals.
                    </p>
                </Message>

                <p className="playbook-text">
                    The attached spreadsheets include a couple scenarios that show how to build out this model.
                </p>
            </Segment>

            <Segment className="playbook-section">
                <Header as="h2" className="playbook-section-header">
                    Example Comp Plans
                </Header>
                
                <Card fluid>
                    <Card.Content>
                        <Card.Header>
                            <Icon name="download" />
                            Sample Compensation Plan Spreadsheet
                        </Card.Header>
                        <Card.Description>
                            <p>Download our sample compensation plan templates that demonstrate multiple scenarios for building effective comp structures.</p>
                            <Button 
                                primary 
                                icon="download" 
                                labelPosition="left"
                                content="Download Sample Comp Plan"
                                onClick={handleDownload}
                            />
                        </Card.Description>
                    </Card.Content>
                </Card>
            </Segment>
        </Container>
    );
});

export default CompPlans;